import { Button } from "@/shared";
import { AppSidebar, CentralSearch, Footer } from "@/shared/components";
import { CodeScannerTabbedDialog } from "@/shared/components";
import { SidebarProvider, SidebarTrigger } from "@/shared/ui/sidebar";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { QrCode, UserCheck, ArrowLeft } from "lucide-react";
import { useAuthStore } from "@/features/auth";
import { useUserScopeStore } from "@/features/auth/model/user-scope.store";
import { userScopeApi } from "@/entities/auth";
import { isSystemAdmin, isTechnicalUser, isTenantUser, isLocationUser } from "@/shared/types";
import { useTenantStore } from "@/features/tenant/model";
import {
  useEffectiveLocationId,
  isPathAllowedForTechnicalRole,
  isPathAllowedForLocationUser,
} from "./guards";
import { ImpersonationBanner, ImpersonationModal } from "@/features/user/components";
import { useImpersonationStore } from "@/features/user/model/impersonationStore";
import { useWorkRoleStore } from "@/features/user/model/workRoleStore";
import { workContextToBackendRole } from "@/features/user/utils/role-mapping";
import { useLanguageStore } from "@/app/store";
import { LanguageSwitcher } from "@/shared/components";
import { useQueryClient } from "@tanstack/react-query";
import logo from "@/assets/logo.png";
import { useRealtimeStream } from "@/entities/account";
import { accountQueryKeys } from "@/entities/account/model/accountQueryKeys";
import { getMenuKeyFromPath } from "./guards";
import { SentryRouteTracker } from "@/core/sentry";

const UI_LANGUAGES = ["de", "en", "sr"] as const;

// Map auth role (backend PascalCase) to system role string expected by impersonation store
function authRoleToSystemRole(
  role?: string | null
): "SYSTEM_ADMIN" | "ACCOUNT_ADMIN" | "NORMAL_USER" {
  if (role === "SystemAdmin") return "SYSTEM_ADMIN";
  if (role === "AccountAdmin") return "ACCOUNT_ADMIN";
  return "NORMAL_USER";
}

/** Minimal layout for Location User: no sidebar, header with logout only. No location link. Shows impersonation banner when admin is impersonating. */
function LocationUserLayout() {
  const { t } = useTranslation("common");
  const effectiveLocationId = useEffectiveLocationId();
  const { isImpersonating } = useImpersonationStore();

  if (!effectiveLocationId) {
    return (
      <div className="flex min-h-screen w-full flex-col">
        {isImpersonating() && <ImpersonationBanner />}
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between gap-4 border-b bg-background px-4 py-px">
          <img src={logo} alt="Lury OS" className="h-8" />
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center p-6">
          <p className="max-w-md text-center text-muted-foreground">
            {t("locationUser.noLocationAssigned")}
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col">
      {isImpersonating() && <ImpersonationBanner />}
      <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between gap-4 border-b bg-background px-4 py-px">
        <img src={logo} alt="Lury OS" className="h-8" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
        </div>
      </header>
      <main className="min-w-0 flex-1 overflow-x-hidden p-6">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

const AppLayout = () => {
  const { t } = useTranslation(["common", "pages"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [scannerOpen, setScannerOpen] = useState(false);
  const { user } = useAuthStore();
  const { scope, setScope, clearScope } = useUserScopeStore();
  const { syncCurrentTenantFromAuth } = useTenantStore();
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const {
    isImpersonating,
    impersonatedContext,
    impersonatedUserName,
    impersonatedUserRole,
    stopImpersonation,
    realAdminContext,
  } = useImpersonationStore();
  const { setCurrentContext } = useWorkRoleStore();
  const effectiveRole =
    isImpersonating() && impersonatedContext
      ? workContextToBackendRole(impersonatedContext)
      : (user?.role ?? null);
  const effectiveLocationId = useEffectiveLocationId();
  const userIsSystemAdmin = isSystemAdmin(effectiveRole);
  const userIsLocationUser = isLocationUser(effectiveRole);

  // Apply user's preferred language when session is restored (e.g. page refresh)
  useEffect(() => {
    if (
      user?.preferredLanguage &&
      UI_LANGUAGES.includes(user.preferredLanguage as (typeof UI_LANGUAGES)[number])
    ) {
      setLanguage(user.preferredLanguage as (typeof UI_LANGUAGES)[number]);
    }
  }, [user?.preferredLanguage, setLanguage]);

  // Load scope when missing for authenticated tenant users (e.g. page refresh, stale storage)
  useEffect(() => {
    if (!user || !isTenantUser(user.role) || scope !== null) return;
    userScopeApi.buildUserScope().then(setScope).catch(clearScope);
  }, [user, scope, setScope, clearScope]);

  // Sync tenant on mount and when user changes
  useEffect(() => {
    syncCurrentTenantFromAuth();
  }, [user, syncCurrentTenantFromAuth]);

  // Mid-session impersonation redirect: when impersonation starts, the loader
  // won't re-run, so we redirect here if the current page is now restricted.
  useEffect(() => {
    if (isTechnicalUser(effectiveRole) && !isPathAllowedForTechnicalRole(location.pathname)) {
      navigate("/technician-dashboard", { replace: true });
    } else if (
      isLocationUser(effectiveRole) &&
      effectiveLocationId &&
      !isPathAllowedForLocationUser(location.pathname, effectiveLocationId)
    ) {
      navigate(`/branches/${effectiveLocationId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRole, effectiveLocationId]);

  // Rebuild scope + invalidate caches whenever the backend pushes a realtime event.
  // Both account-module changes and permission changes require the same recovery:
  // re-fetch the user's scope and redirect away from any route that became inaccessible.
  const handleScopeRefresh = async () => {
    try {
      const newScope = await userScopeApi.buildUserScope();
      setScope(newScope);

      await queryClient.invalidateQueries({ queryKey: accountQueryKeys.myAccount() });
      await queryClient.invalidateQueries({ queryKey: accountQueryKeys.accountPrefix() });

      const menuKey = getMenuKeyFromPath(location.pathname);
      if (menuKey) {
        const { canAccessMenuItem } = useUserScopeStore.getState();
        if (!canAccessMenuItem(menuKey)) {
          navigate("/", { replace: true });
        }
      }
    } catch (err) {
      console.error(`[SSE] Failed to refresh scope after event`, err);
    }
  };

  useRealtimeStream({
    onModuleChanged: () => handleScopeRefresh(),
    onPermissionChanged: () => handleScopeRefresh(),
  });

  const { openModal, canImpersonate } = useImpersonationStore();

  // Show Demo Login when the logged-in user (auth) is an admin who can impersonate
  const realSystemRole = authRoleToSystemRole(user?.role);
  const showImpersonateButton = canImpersonate(realSystemRole);

  const handleStopImpersonation = async () => {
    if (realAdminContext) {
      setCurrentContext(realAdminContext);
    }
    stopImpersonation();

    try {
      const newScope = await userScopeApi.buildUserScope();
      setScope(newScope);
    } catch {
      clearScope();
    }

    queryClient.clear();
  };

  if (userIsLocationUser) {
    return <LocationUserLayout />;
  }

  return (
    <>
      <SidebarProvider>
        <div className="flex min-h-screen w-full min-w-0">
          <AppSidebar />

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-background px-4 py-px">
              <SidebarTrigger />

              {!userIsSystemAdmin && <CentralSearch />}

              {isImpersonating() && (
                <div className="hidden items-center gap-3 font-medium text-foreground sm:flex">
                  <UserCheck className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <span>
                    {t("pages:impersonation.banner", "Acting as")}{" "}
                    <strong>{impersonatedUserName}</strong>
                    {impersonatedUserRole && (
                      <span className="opacity-75"> ({impersonatedUserRole})</span>
                    )}
                  </span>
                </div>
              )}

              <div className="ml-auto flex items-center gap-2">
                {showImpersonateButton && !isImpersonating() && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openModal()}
                    title={t("pages:impersonation.title", "Open as user")}
                    className="hidden sm:flex"
                  >
                    <UserCheck className="mr-2 h-4 w-4" />
                    {t("pages:impersonation.menuItem", "Open as user")}
                  </Button>
                )}
                {showImpersonateButton && isImpersonating() && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStopImpersonation}
                    title={t("pages:impersonation.stopButton", "Back to Admin")}
                    className="border-warning bg-warning/10 hover:bg-warning/20 text-warning-foreground hidden sm:flex"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t("pages:impersonation.stopButton", "Back to Admin")}
                  </Button>
                )}

                {!userIsSystemAdmin && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setScannerOpen(true)}
                    title={t("common:scanner.title")}
                  >
                    <QrCode className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </header>

            <main className="min-w-0 flex-1 overflow-x-hidden p-6">
              <Outlet />
            </main>

            <Footer />
          </div>
        </div>

        {!userIsSystemAdmin && (
          <CodeScannerTabbedDialog
            open={scannerOpen}
            onOpenChange={setScannerOpen}
            defaultTab="qr"
          />
        )}

        {/* Impersonation Modal */}
        <ImpersonationModal />
      </SidebarProvider>
    </>
  );
};

export function LayoutWrapper() {
  return (
    <>
      <SentryRouteTracker />
      <AppLayout />
    </>
  );
}
